import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface UserPermissions {
  permissions: string[];
  uid: number;
  username: string;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private permissionsSubject = new BehaviorSubject<string[]>([]);
  private userSubject = new BehaviorSubject<UserPermissions | null>(null);

  constructor() {
    // Load permissions from localStorage if available
    this.loadPermissionsFromStorage();
  }

  /**
   * Set user permissions (called after login)
   */
  setUserPermissions(userData: any): void {
    const permissions = userData.permissions || [];
    const userPermissions: UserPermissions = {
      permissions: permissions,
      uid: userData.userid || userData.uid,
      username: userData.name || userData.username
    };

    this.permissionsSubject.next(permissions);
    this.userSubject.next(userPermissions);

    // Store in localStorage
    localStorage.setItem('userPermissions', JSON.stringify(userPermissions));
  }

  /**
   * Clear user permissions (called on logout)
   */
  clearUserPermissions(): void {
    this.permissionsSubject.next([]);
    this.userSubject.next(null);
    localStorage.removeItem('userPermissions');
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(permission: string): boolean {
    const permissions = this.permissionsSubject.value;
    return permissions.includes(permission);
  }

  /**
   * Check if user can access a module
   */
  canAccessModule(module: string): boolean {
    return this.hasPermission(`mod_${module}`);
  }

  /**
   * Check if user can perform a specific action
   */
  canPerformAction(action: string): boolean {
    return this.hasPermission(`do_${action}`);
  }

  /**
   * Get all user permissions as observable
   */
  getPermissions(): Observable<string[]> {
    return this.permissionsSubject.asObservable();
  }

  /**
   * Get current user data as observable
   */
  getUser(): Observable<UserPermissions | null> {
    return this.userSubject.asObservable();
  }

  /**
   * Get current permissions synchronously
   */
  getCurrentPermissions(): string[] {
    return this.permissionsSubject.value;
  }

  /**
   * Get current user synchronously
   */
  getCurrentUser(): UserPermissions | null {
    return this.userSubject.value;
  }

  /**
   * Load permissions from localStorage
   */
  private loadPermissionsFromStorage(): void {
    const stored = localStorage.getItem('userPermissions');
    if (stored) {
      try {
        const userPermissions: UserPermissions = JSON.parse(stored);
        this.permissionsSubject.next(userPermissions.permissions);
        this.userSubject.next(userPermissions);
      } catch (error) {
        console.error('Error loading permissions from storage:', error);
        this.clearUserPermissions();
      }
    }
  }

  /**
   * Check if user has any of the specified permissions
   */
  hasAnyPermission(permissions: string[]): boolean {
    const userPermissions = this.permissionsSubject.value;
    return permissions.some(permission => userPermissions.includes(permission));
  }

  /**
   * Check if user has all of the specified permissions
   */
  hasAllPermissions(permissions: string[]): boolean {
    const userPermissions = this.permissionsSubject.value;
    return permissions.every(permission => userPermissions.includes(permission));
  }

  /**
   * Get module access permissions
   */
  getModulePermissions(): string[] {
    const permissions = this.permissionsSubject.value;
    return permissions.filter(permission => permission.startsWith('mod_'));
  }

  /**
   * Get action permissions
   */
  getActionPermissions(): string[] {
    const permissions = this.permissionsSubject.value;
    return permissions.filter(permission => permission.startsWith('do_'));
  }

  /**
   * Check if user is admin (has all permissions)
   */
  isAdmin(): boolean {
    const permissions = this.permissionsSubject.value;
    // Admin has all module and action permissions
    const allModulePermissions = [
      'mod_clients', 'mod_mandat', 'mod_transfer', 'mod_mitarbeiter',
      'mod_vstv', 'mod_reports', 'mod_settings'
    ];
    const allActionPermissions = [
      'do_send_refamail', 'do_approve_mandat', 'do_export_data',
      'do_delete_records', 'do_view_audit', 'do_manage_users', 'do_manage_roles'
    ];
    
    return this.hasAllPermissions([...allModulePermissions, ...allActionPermissions]);
  }
} 